import BaseController from '../BaseController';
import { OnBeforeSubmit, OnChange, OnClear, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnClick, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, ChangeEvent, ClickEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Inject } from '@odinjs/odin';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../constants/endpoints';
import { ApplicationRoute } from '../../constants/routes';
import { Value } from '@philips-internal/oswald-ui-core';
import { BaseToastProps, CheckboxAPI, FieldAPI, FilterAPI, MapperAPI, ToastService } from '@philips-internal/oswald-ui-components';
import ShowToast from '../../utils/toast-service';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import './patient-list.css';
import { FragmentNames, PatientListAliases } from '../../codegen/';
import { ImageUtils } from '../../utils/image-utils';
import {DataFilter, PatientMapper} from './patient-list-types';
import { DateFilterUtils } from '../../utils/date-filter-utils';
import { FunctionName, PEP_281Parameters } from '../../utils/parameters';
import moment from 'moment';
import { YesNoType } from '../../utils/enum-utils';
import InformationBarUtils from '../commons/header/information-bar-header-utils';
import { FoundationUtils } from '../../utils/foundation-utils';
import timeService from '../../services/time-service';
import { FULL_LOCALE_DATE } from '../../constants/date-utils';

const initialFilterData = {
  ieMeusPacientes: false,
  iePacientesRecentes: true
}

const toastErrorId                   = 'patient-list-controller.no-access-to-encounter';
const messageNoAccessEncounter       = 'text.no-access-to-encounter';
const messageNoAccessEncounterSector = 'text.no-access-to-encounter-sector';
const messageFilterPerformance       = 'text.filter-performance';
const messageMoreThanNinetyDays      = 'text.message.more-ninety-days';

interface InformationBarData {
  deathDate?: string;
  dischargedDate?: string;
}

@Controller({ component: PatientListAliases.PATIENT_LIST_LAYOUT, fragment: FragmentNames.PATIENT_LIST })
class PatientListController extends BaseController {

  @Inject(PatientListAliases.FILTER_PATIENTS)
  private filterAtendimentosAPI!: FilterAPI;

  @Inject(PatientListAliases.FILTER_FIELD_DISCHARGED_PERIOD)
  private fieldDischargedPeriodAPI!: FieldAPI;

  @Inject(PatientListAliases.FIELD_FROM_DATE)
  private fieldFromDateAPI!: FieldAPI;

  @Inject(PatientListAliases.FIELD_TO_DATE)
  private fieldToDateAPI!: FieldAPI;

  internalNavigation!: boolean;

  @OnClear({ target: PatientListAliases.FILTER_PATIENTS })
  onFilterClear(): void {
    this.fieldDischargedPeriodAPI.setVisible(false);
    this.fieldFromDateAPI.setVisible(false);
    this.fieldToDateAPI.setVisible(false);
  }

  @OnMount(PatientListAliases.PATIENT_LIST_LAYOUT)
  handleMountLayout(): void {
    this.internalNavigation = false;

    timeService.getCurrentDateTime()
      .then(currentDateTime => {
        const referenceDateTime = moment(currentDateTime).format(FULL_LOCALE_DATE);
        this.context.set({ currentDateTime: referenceDateTime })
      })
      .catch(() => this.context.set({ currentDateTime: moment().format(FULL_LOCALE_DATE) }));
  }

  @OnMount(PatientListAliases.PATIENT_FETCH_MAPPER)
  patientFetchMapper(onMountEvent: MountEvent): void {
    const mapperEvent = onMountEvent.target as MapperAPI;

    mapperEvent.addCustomMapping('subtitle', data  => {
      const dataItem = data as unknown as PatientMapper;
      const bornDate = moment(dataItem.bornDate).format('DD-MM-YYYY');
      return `${bornDate} / ${dataItem.genero} / ${dataItem.nrAtendimento}`;
    });

    mapperEvent.addCustomMapping('photo', data  => {
      const dataItem = data as unknown as PatientMapper;
      return ImageUtils.getImageAvatar(dataItem.photo as string)
    });
  }

  @OnMount({ target: PatientListAliases.FILTER_PATIENTS })
  onFilterMount(): void {
    /**
     * Workaround due a bug in the filter's restoreState
     */
    const data = this.filterAtendimentosAPI.restoreState(true) ? this.filterAtendimentosAPI.getRecord() : initialFilterData;

    this.filterAtendimentosAPI.clear();
    this.filterAtendimentosAPI.setData(data as Record<string, Value>);

    this.internalNavigation = false;
  }

  @OnMount(PatientListAliases.FILTER_FIELD_DISCHARGED_PERIOD)
  handleMountFieldPeriod(): void {
    const check = this.filterAtendimentosAPI.getRecord() as DataFilter;

    if (check?.ieDischargedPatients) {
      this.fieldDischargedPeriodAPI.setVisible(true);
      return;
    }

    this.fieldDischargedPeriodAPI.setVisible(false);
  }

  private setVisibleFields(fieldItem: FieldAPI): void {
    const check = this.filterAtendimentosAPI.getRecord() as DataFilter;
    check?.ieDischargedPatients ? fieldItem.setVisible(true) : fieldItem.setVisible(false);
  }

  @OnMount(PatientListAliases.FIELD_FROM_DATE)
  handleMountFromDate(): void {
    this.setVisibleFields(this.fieldFromDateAPI);
  }

  @OnMount(PatientListAliases.FIELD_TO_DATE)
  handleMountToDate(): void {
    this.setVisibleFields(this.fieldToDateAPI);
  }

  @OnChange(PatientListAliases.CHECKBOX_ONLY_DISCHARGED_PATIENTS)
  handleChangeDischargedPatients(event: ChangeEvent<CheckboxAPI>): void {
    if (event.value) {
      this.fieldDischargedPeriodAPI.setVisible(true);
      this.fieldFromDateAPI.setVisible(true);
      this.fieldToDateAPI.setVisible(true);
      return;
    }
    this.fieldDischargedPeriodAPI.setVisible(false);
    this.fieldFromDateAPI.setVisible(false);
    this.fieldToDateAPI.setVisible(false);
    this.fieldFromDateAPI.setValue('');
    this.fieldToDateAPI.setValue('');
  }

  @OnUnmount({ target: PatientListAliases.FILTER_PATIENTS })
  onFilterUnmount(event: MountEvent): void {
    const filterAtendimentosAPI = event.target as FilterAPI;
    filterAtendimentosAPI.persistState();
  }

  @OnClick({ target: PatientListAliases.PATIENT_FETCH_DECK })
  onCardClick(event: ClickEvent): void {
    const { cdPessoaFisica: cdPaciente, nrAtendimento, dischargedDate } = event.value;

    HttpService.get(Endpoint.USER_HAS_EMR_ACCESS_TO_ENCOUNTER, { cdPaciente, encounterId: nrAtendimento })
      .then(() => {

        if (!dischargedDate) {
          HttpService.get(Endpoint.USER_HAS_EMR_ACCESS_TO_ENCOUNTER_SECTOR, { encounterId: nrAtendimento })
            .then(() => {
              this.handleRedirectAttendance(cdPaciente, nrAtendimento);
            }).catch(() => this.showErrorToast(messageNoAccessEncounterSector));
        } else {
          this.handleRedirectAttendance(cdPaciente, nrAtendimento);
        }
      }).catch(() =>  this.showErrorToast(messageNoAccessEncounter));
  }

  private handleRedirectAttendance(cdPaciente: string, nrAtendimento: number): void {
    const actualModule = applicationContext.getCurrentModule();
    const route = actualModule && 'module.cpoe'.localeCompare(actualModule.name) === 0 ?
      ApplicationRoute.MODULE_CPOE :
      ApplicationRoute.MODULE_ELECTRONIC_PATIENT_CHART;

    HttpService.get(Endpoint.EMAR_FIND_PATIENT_INFOS, {encounterId: nrAtendimento, patientId: cdPaciente}).then(data => {
      const info = data.body as InformationBarData;
      const dischargedDate = (info.dischargedDate) ? moment(info.dischargedDate).format('L LTS') : '';
      const deathDate = (info.deathDate) ? moment(info.deathDate).format('L LTS') : '';
      InformationBarUtils.allowInfo  = this.verifyAllowEditRecords(dischargedDate, deathDate);

      applicationContext.redirect(route, {
        encounterId: nrAtendimento,
        patientId: cdPaciente,
      });
    });
  }

  verifyAllowEditRecords(dischargedDate : string, deathDate: string) : boolean {

    if (deathDate) {
      return false;
    }

    if (!dischargedDate) {
      return true;
    }

    const param177 = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.ALLOW_INFO_AFTER_DISCHARGE) === YesNoType.YES;
    const param352 = Number(this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.MINUTES_ADD_INFO_AFTER_DISCHARGE));
    const currentDateTime = this.context.get('currentDateTime');
    const allowInfoAfterDischarge = ((param177) && ((param352 === 0) || ((param352 > 0) && (moment(dischargedDate).add(param352, 'm').isAfter(moment(currentDateTime))))));

    return allowInfoAfterDischarge;
  }

  getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  @OnBeforeSubmit(PatientListAliases.FILTER_PATIENTS)
  handleBeforeSubmit(event: AbortableSubmitEvent): void {

    if (this.internalNavigation) {
      const params = {
        cdSetorAtendimento: event.record?.cdSetorAtendimento,
        cdTipoAtendimento: event.record?.cdTipoAtendimento,
        dateEnd: event.record?.dateEnd,
        dateStart: event.record?.dateStart,
        ieDischargedPatients: event.record?.ieDischargedPatients,
        ieMeusPacientes: event.record?.ieMeusPacientes,
        iePacientesRecentes: event.record?.iePacientesRecentes,
        nmPaciente: event.record?.nmPaciente,
        nrAtendimento: event.record?.nrAtendimento,
        nrProntuario: event.record?.nrProntuario
      };

      const limitDays = 90;
      const validationFilter = ((!params.cdSetorAtendimento)   &&
                                (!params.cdTipoAtendimento)    &&
                                (!params.dateEnd)              &&
                                (!params.dateStart)            &&
                                (!params.ieDischargedPatients) &&
                                (!params.ieMeusPacientes)      &&
                                (!params.iePacientesRecentes)  &&
                                (!params.nmPaciente)           &&
                                (!params.nrAtendimento)        &&
                                (!params.nrProntuario));

      if (validationFilter) {
        ShowToast.open(MessageService.translate(messageFilterPerformance), ToastLevel.ERROR);
        event.abort();
      }

      if (DateFilterUtils.diffDate(params.dateEnd, params.dateStart) > limitDays) {
        ShowToast.open(MessageService.translate(messageMoreThanNinetyDays), ToastLevel.CAUTION);
      }
    }

    this.internalNavigation = true;
  }

  showErrorToast(message: string): void {
    const toast: BaseToastProps = {
      dismissible: true,
      id: toastErrorId,
      level: ToastLevel.ERROR,
      message: MessageService.translate(message),
      showIcon: true,
      timeout: 5000,
      type: ToastType.BUBBLE
    };
    ToastService.open(toast)
  }

}

export default PatientListController;
